import { apiRequest } from "@/lib/api";
import type { BroadcastMode, PricingMode } from "@/lib/genrl";
import type { ProviderCreditStatus } from "@/lib/provider-credit";

export type ProAccountPlan = {
  user_id: string;
  mode: "standard" | "pro";
  pro_enabled_at?: string;
  pro_terms_accepted_at?: string;
  mfa_required: boolean;
  payout_status: "missing" | "pending" | "verified" | "blocked" | string;
  marketplace_status: "disabled" | "pending_review" | "active" | "suspended" | string;
  created_at: string;
  updated_at: string;
};

export type ProProviderConnection = {
  id: string;
  wallet_account_id: string;
  provider_slug: string;
  provider_name: string;
  display_name: string;
  credential_last4?: string;
  credential_version: number;
  has_credential: boolean;
  auth_mode?: string;
  principal_mask?: string;
  account?: Record<string, unknown>;
  credential_status?: Record<string, unknown>;
  status: "validating" | "active" | "degraded" | "revoked" | "suspended" | string;
  visibility: "private" | "network" | "marketplace" | string;
  broadcast_mode: BroadcastMode;
  pricing_mode: PricingMode;
  provider_adjustment_bps: number;
  fixed_unit_price_micros?: number;
  min_unit_price_micros?: number;
  max_unit_price_micros?: number;
  reserve_balance_threshold_micros?: number;
  broadcast_stock_limit?: number;
  allowed_regions?: string[];
  allowed_hardware?: string[];
  capabilities?: Record<string, unknown>;
  health?: Record<string, unknown>;
  provider_balance_micros?: number;
  provider_balance_currency?: string;
  provider_balance_last_checked_at?: string;
  provider_balance_kind?: "credit" | "postpaid" | string;
  provider_balance_status?: "available" | "billing_enabled" | "billing_disabled" | "checking" | "unavailable" | string;
  credit_status?: ProviderCreditStatus | null;
  earned_credit_micros?: number;
  owner_pending_payout_micros?: number;
  settled_jobs?: number;
  last_settlement_at?: string;
  last_validated_at?: string;
  last_error?: string;
  created_at: string;
  updated_at: string;
};

export type ProProviderLedgerEntry = {
  id: string;
  provider_id: string;
  route_type: "internal" | "external" | string;
  hardware: string;
  region?: string;
  status: string;
  earned_credit_micros: number;
  created_at?: string;
  updated_at?: string;
};

export type ProProviderOption = {
  value: string;
  label: string;
  disabled?: boolean;
  hint?: string;
};

export type ProProviderAuthInput = {
  mode: string;
  properties?: Record<string, string>;
  secrets?: Record<string, string | null>;
};

export type ProProviderCreateInput = {
  provider_slug: string;
  provider_name?: string;
  display_name?: string;
  api_key?: string;
  auth?: ProProviderAuthInput;
  account?: Record<string, unknown>;
  visibility?: "private" | "network" | "marketplace";
  broadcast_mode?: BroadcastMode;
  pricing_mode?: PricingMode;
  provider_adjustment_bps?: number;
  fixed_unit_price_micros?: number;
  min_unit_price_micros?: number;
  max_unit_price_micros?: number;
  reserve_balance_threshold_micros?: number;
  broadcast_stock_limit?: number;
  allowed_regions?: string[];
  allowed_hardware?: string[];
};

export type ProProviderCreditSource = {
  id?: string;
  provider_connection_id?: string;
  billing_account_name: string;
  source_mode: "normalized_view" | "direct_table";
  query_project_id: string;
  dataset_project_id: string;
  dataset_id: string;
  source_object_id: string;
  coverage_object_id?: string | null;
  location: string;
  credential_reused: boolean;
  credential_configured?: boolean;
  coverage_start_at?: string | null;
  max_bytes_billed?: number;
  configuration_status?: string;
  last_success_at?: string | null;
  last_error_code?: string | null;
  last_error_safe_message?: string | null;
  version?: number;
};

export type ProProviderCreditSourceInput = {
  source_mode: "normalized_view" | "direct_table";
  query_project_id: string;
  dataset_project_id: string;
  dataset_id: string;
  source_object_id: string;
  coverage_object_id?: string;
  location: string;
  billing_account_id: string;
  reuse_provider_credential?: boolean;
  auth?: ProProviderAuthInput;
  coverage_start_at?: string;
  max_bytes_billed?: number;
  version?: number;
};

export type ProProviderCreditGrant = {
  id: string;
  baseline_mode: "original_grant" | "console_observation";
  google_credit_id?: string | null;
  google_credit_full_name?: string | null;
  grant_type: "one_time" | "recurring" | "unknown";
  original_amount_micros?: string | null;
  observed_remaining_micros?: string | null;
  currency: string;
  starts_at: string;
  ends_at?: string | null;
  scope_description?: string | null;
  verification_level?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
};

export type ProProviderCreditGrantInput = {
  baseline_mode: "original_grant" | "console_observation";
  google_credit_id?: string;
  google_credit_full_name?: string;
  grant_type: "one_time" | "recurring" | "unknown";
  original_amount_micros?: string;
  observed_remaining_micros?: string;
  currency: string;
  starts_at: string;
  ends_at?: string;
  scope_description?: string;
  attested: boolean;
};

export type ProProviderCreditJob = {
  id: string;
  provider_connection_id: string;
  trigger: string;
  status:
    | "queued"
    | "running"
    | "succeeded"
    | "failed"
    | "skipped"
    | string;
  started_at?: string | null;
  completed_at?: string | null;
  export_watermark_after?: string | null;
  estimated_bytes?: number | null;
  billed_bytes?: number | null;
  row_count?: number | null;
  error_code?: string | null;
  error_safe_message?: string | null;
};

export type ProProviderCreditOnboardingState =
  | "needs_billing_account"
  | "needs_dataset_project"
  | "needs_export"
  | "source_choice_required"
  | "syncing"
  | "source_configured"
  | "permission_required"
  | "disabled"
  | "unavailable"
  | "error";

export type ProProviderCreditOnboardingNextAction =
  | "set_billing_account"
  | "enter_dataset_project"
  | "configure_export"
  | "configure_source"
  | "select_source"
  | "wait"
  | "refresh"
  | "grant_bigquery_access"
  | "contact_admin"
  | "retry";

export type ProProviderCreditExportCandidate = {
  billing_account_id: string;
  dataset_project_id: string;
  dataset_id: string;
  source_object_id: string;
  location: string;
  source_mode: "direct_table" | "normalized_view";
};

export type ProProviderCreditOnboarding = {
  state: ProProviderCreditOnboardingState;
  next_action: ProProviderCreditOnboardingNextAction;
  safe_message: string;
  billing_account_name?: string | null;
  currency?: string | null;
  source?: ProProviderCreditSource | null;
  candidates?: ProProviderCreditExportCandidate[];
  credit_status?: ProviderCreditStatus | null;
  job?: {
    id: string;
    status: string;
    trigger: string;
  } | null;
};

export const PRO_PROVIDER_OPTIONS: ProProviderOption[] = [
  { value: "runpod", label: "RunPod" },
  { value: "hyperstack", label: "Hyperstack" },
  { value: "lambda", label: "Lambda Labs" },
];

export const WALLET_MARKET_PROVIDER_OPTIONS: ProProviderOption[] = [
  ...PRO_PROVIDER_OPTIONS,
  { value: "gcp", label: "Google Cloud" },
];

export function upsertProProviderConnection(
  providers: ProProviderConnection[],
  provider: ProProviderConnection,
) {
  const existingIndex = providers.findIndex((candidate) => candidate.id === provider.id);
  if (existingIndex < 0) return [provider, ...providers];
  return providers.map((candidate, index) => index === existingIndex ? provider : candidate);
}

export function resolveProProviderRefreshResult(
  fetched: ProProviderConnection[],
  current: ProProviderConnection[],
  requestMutationVersion: number,
  currentMutationVersion: number,
) {
  return requestMutationVersion === currentMutationVersion ? fetched : current;
}

export async function readProAccountPlan() {
  return apiRequest<{ plan: ProAccountPlan }>("/api/wallet/pro/plan", { method: "GET" });
}

export async function updateProAccountPlan(payload: { mode: "standard" | "pro"; accept_pro_terms?: boolean }) {
  return apiRequest<{ plan: ProAccountPlan }>("/api/wallet/pro/plan", {
    method: "PATCH",
    body: payload,
  });
}

export async function readProProviders() {
  return apiRequest<{ providers: ProProviderConnection[] }>("/api/wallet/pro/providers", {
    method: "GET",
    cache: "no-store",
  });
}

export async function readProProviderLedger(providerID: string) {
  return apiRequest<{ provider_id: string; entries: ProProviderLedgerEntry[] }>(
    `/api/wallet/pro/providers/${encodeURIComponent(providerID)}/ledger`,
    { method: "GET", timeoutMs: 20000, cache: "no-store" },
  );
}

export async function createProProvider(payload: ProProviderCreateInput) {
  return apiRequest<{ provider: ProProviderConnection }>("/api/wallet/pro/providers", {
    method: "POST",
    body: payload,
    timeoutMs: 70_000,
  });
}

export function createProProviderCreditDiscoveryIdempotencyKey(
  providerID: string,
) {
  const randomID =
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const providerHint = String(providerID || "")
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .slice(-24);
  return `gcp-credit-discovery-${providerHint || "provider"}-${randomID}`;
}

export async function discoverProProviderCredit(
  providerID: string,
  input: { dataset_project_id?: string; dataset_id?: string } = {},
  idempotencyKey = createProProviderCreditDiscoveryIdempotencyKey(providerID),
) {
  const datasetProjectID = String(input.dataset_project_id || "").trim();
  const datasetID = String(input.dataset_id || "").trim();
  return apiRequest<{ credit_onboarding: ProProviderCreditOnboarding }>(
    `${proProviderCreditPath(providerID)}/credit-discovery`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: {
        ...(datasetProjectID
          ? { dataset_project_id: datasetProjectID }
          : {}),
        ...(datasetID ? { dataset_id: datasetID } : {}),
      },
      cache: "no-store",
      timeoutMs: 70_000,
    },
  );
}

const GOOGLE_CREDIT_ONBOARDING_SESSION_PREFIX =
  "genrl.google_credit_onboarding.v1.";
const GOOGLE_CREDIT_ONBOARDING_SESSION_TTL_MS = 15 * 60 * 1000;

function proCreditRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function proCreditSafeText(
  value: unknown,
  maximumLength = 512,
  required = false,
) {
  const text = typeof value === "string" ? value.trim() : "";
  if (
    (required && !text) ||
    text.length > maximumLength ||
    /[\u0000-\u001f\u007f]/.test(text)
  ) {
    return null;
  }
  return text;
}

function proCreditCandidate(
  value: unknown,
): ProProviderCreditExportCandidate | null {
  const record = proCreditRecord(value);
  if (!record) return null;
  const billingAccountID = proCreditSafeText(
    record.billing_account_id,
    64,
    true,
  );
  const datasetProjectID = proCreditSafeText(
    record.dataset_project_id,
    128,
    true,
  );
  const datasetID = proCreditSafeText(record.dataset_id, 1024, true);
  const sourceObjectID = proCreditSafeText(
    record.source_object_id,
    1024,
    true,
  );
  const location = proCreditSafeText(record.location, 128, true);
  const sourceMode = proCreditSafeText(record.source_mode, 32, true);
  if (
    !billingAccountID ||
    !datasetProjectID ||
    !datasetID ||
    !sourceObjectID ||
    !location ||
    !["direct_table", "normalized_view"].includes(sourceMode || "")
  ) {
    return null;
  }
  return {
    billing_account_id: billingAccountID,
    dataset_project_id: datasetProjectID,
    dataset_id: datasetID,
    source_object_id: sourceObjectID,
    location,
    source_mode: sourceMode as ProProviderCreditExportCandidate["source_mode"],
  };
}

export function parseProProviderCreditOnboarding(
  value: unknown,
): ProProviderCreditOnboarding | null {
  const record = proCreditRecord(value);
  if (!record) return null;
  const state = proCreditSafeText(record.state, 64, true);
  const nextAction = proCreditSafeText(record.next_action, 64, true);
  const safeMessage = proCreditSafeText(record.safe_message, 2_000, true);
  if (!state || !nextAction || !safeMessage) return null;

  const rawCandidates = record.candidates;
  let candidates: ProProviderCreditExportCandidate[] | undefined;
  if (rawCandidates !== undefined) {
    if (!Array.isArray(rawCandidates) || rawCandidates.length > 20) {
      return null;
    }
    const parsedCandidates = rawCandidates.map(proCreditCandidate);
    if (parsedCandidates.some((candidate) => !candidate)) return null;
    candidates =
      parsedCandidates as ProProviderCreditExportCandidate[];
  }

  const jobRecord = proCreditRecord(record.job);
  const job = jobRecord
    ? {
        id: proCreditSafeText(jobRecord.id, 256, true),
        status: proCreditSafeText(jobRecord.status, 64, true),
        trigger: proCreditSafeText(jobRecord.trigger, 64, true),
      }
    : null;
  if (jobRecord && (!job?.id || !job.status || !job.trigger)) return null;

  return {
    ...(record as unknown as ProProviderCreditOnboarding),
    state: state as ProProviderCreditOnboardingState,
    next_action: nextAction as ProProviderCreditOnboardingNextAction,
    safe_message: safeMessage,
    ...(candidates
      ? { candidates: candidates as ProProviderCreditExportCandidate[] }
      : {}),
    ...(job
      ? {
          job: {
            id: job.id!,
            status: job.status!,
            trigger: job.trigger!,
          },
        }
      : {}),
  };
}

function googleCreditOnboardingSessionKey(providerID: string) {
  const safeID = proCreditSafeText(providerID, 256, true);
  return safeID
    ? `${GOOGLE_CREDIT_ONBOARDING_SESSION_PREFIX}${encodeURIComponent(safeID)}`
    : "";
}

export function storeProProviderCreditOnboarding(
  providerID: string,
  onboarding: ProProviderCreditOnboarding,
) {
  if (typeof window === "undefined") return false;
  const key = googleCreditOnboardingSessionKey(providerID);
  const safeOnboarding = parseProProviderCreditOnboarding(onboarding);
  if (!key || !safeOnboarding) return false;
  try {
    const serialized = JSON.stringify({
      stored_at: Date.now(),
      onboarding: safeOnboarding,
    });
    if (serialized.length > 64 * 1024) return false;
    window.sessionStorage.setItem(key, serialized);
    return true;
  } catch {
    return false;
  }
}

export function consumeProProviderCreditOnboarding(providerID: string) {
  if (typeof window === "undefined") return null;
  const key = googleCreditOnboardingSessionKey(providerID);
  if (!key) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
    if (!raw || raw.length > 64 * 1024) return null;
    const record = proCreditRecord(JSON.parse(raw));
    const storedAt = Number(record?.stored_at);
    if (
      !record ||
      !Number.isFinite(storedAt) ||
      storedAt <= 0 ||
      Date.now() - storedAt > GOOGLE_CREDIT_ONBOARDING_SESSION_TTL_MS
    ) {
      return null;
    }
    return parseProProviderCreditOnboarding(record.onboarding);
  } catch {
    return null;
  }
}

export async function updateProProvider(providerID: string, payload: {
  display_name?: string;
  account?: Record<string, unknown>;
  visibility?: "private" | "network" | "marketplace";
  status?: string;
  broadcast_mode?: BroadcastMode;
  pricing_mode?: PricingMode;
  provider_adjustment_bps?: number;
  fixed_unit_price_micros?: number;
  min_unit_price_micros?: number;
  max_unit_price_micros?: number;
  reserve_balance_threshold_micros?: number;
  broadcast_stock_limit?: number;
  allowed_regions?: string[];
  allowed_hardware?: string[];
}) {
  return apiRequest<{ provider: ProProviderConnection }>(
    `/api/wallet/pro/providers/${encodeURIComponent(providerID)}`,
    { method: "PATCH", body: payload, timeoutMs: 70_000 },
  );
}

export async function validateProProvider(providerID: string) {
  return apiRequest<{ provider: ProProviderConnection }>(
    `/api/wallet/pro/providers/${encodeURIComponent(providerID)}/validate`,
    { method: "POST", timeoutMs: 70_000 },
  );
}

export async function deleteProProvider(providerID: string) {
  return apiRequest<{ deleted: boolean; provider: ProProviderConnection }>(
    `/api/wallet/pro/providers/${encodeURIComponent(providerID)}`,
    { method: "DELETE", timeoutMs: 20000 },
  );
}

export async function rotateProProviderKey(providerID: string, apiKey: string) {
  return apiRequest<{ provider: ProProviderConnection }>(
    `/api/wallet/pro/providers/${encodeURIComponent(providerID)}/rotate-key`,
    {
      method: "POST",
      body: { api_key: apiKey },
      timeoutMs: 70_000,
    },
  );
}

export async function rotateProProviderCredentials(
  providerID: string,
  auth: ProProviderAuthInput,
) {
  return apiRequest<{ provider: ProProviderConnection }>(
    `/api/wallet/pro/providers/${encodeURIComponent(providerID)}/rotate-credentials`,
    {
      method: "POST",
      body: { auth },
      timeoutMs: 70_000,
    },
  );
}

const proProviderCreditPath = (providerID: string) =>
  `/api/v1/pro/providers/${encodeURIComponent(providerID)}`;

export async function readProProviderCreditSource(providerID: string) {
  return apiRequest<{ source?: ProProviderCreditSource | null }>(
    `${proProviderCreditPath(providerID)}/credit-source`,
    {
      method: "GET",
      cache: "no-store",
      timeoutMs: 20_000,
    },
  );
}

export async function readProProviderCreditStatus(providerID: string) {
  return apiRequest<{ credit_status: ProviderCreditStatus }>(
    `${proProviderCreditPath(providerID)}/credit-status`,
    {
      method: "GET",
      cache: "no-store",
      timeoutMs: 20_000,
    },
  );
}

export async function upsertProProviderCreditSource(
  providerID: string,
  source: ProProviderCreditSourceInput,
) {
  return apiRequest<{
    source: ProProviderCreditSource;
    job?: { id: string; status: string; trigger: string };
  }>(`${proProviderCreditPath(providerID)}/credit-source`, {
    method: "PUT",
    body: source,
    timeoutMs: 70_000,
  });
}

export async function deleteProProviderCreditSource(providerID: string) {
  return apiRequest<void>(`${proProviderCreditPath(providerID)}/credit-source`, {
    method: "DELETE",
    timeoutMs: 20_000,
  });
}

export async function testProProviderCreditSource(providerID: string) {
  return apiRequest<{
    job: {
      id: string;
      status: string;
      provider_connection_id: string;
      trigger: string;
    };
  }>(`${proProviderCreditPath(providerID)}/credit-source-test`, {
    method: "POST",
    timeoutMs: 70_000,
  });
}

export async function refreshProProviderCreditStatus(providerID: string) {
  return apiRequest<{
    job: {
      id: string;
      status: string;
      provider_connection_id: string;
      trigger: string;
    };
  }>(`${proProviderCreditPath(providerID)}/credit-refresh`, {
    method: "POST",
    timeoutMs: 20_000,
  });
}

export async function readProProviderCreditJob(
  providerID: string,
  jobID: string,
) {
  return apiRequest<{ job: ProProviderCreditJob }>(
    `${proProviderCreditPath(providerID)}/credit-jobs/${encodeURIComponent(jobID)}`,
    {
      method: "GET",
      cache: "no-store",
      timeoutMs: 20_000,
    },
  );
}

export async function waitForProProviderCreditJob(
  providerID: string,
  jobID: string,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    delay?: (milliseconds: number) => Promise<void>;
  } = {},
) {
  const intervalMs = Math.max(250, options.intervalMs ?? 1_500);
  const maxAttempts = Math.max(1, options.maxAttempts ?? 300);
  const delay =
    options.delay ??
    ((milliseconds: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, milliseconds)));

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const payload = await readProProviderCreditJob(providerID, jobID);
    const status = String(payload.job?.status || "").toLowerCase();
    if (["succeeded", "failed", "skipped"].includes(status)) {
      return payload.job;
    }
    if (attempt + 1 < maxAttempts) {
      await delay(intervalMs);
    }
  }
  throw new Error(
    "Google Cloud credit refresh did not finish before the polling deadline.",
  );
}

export async function readProProviderCreditGrants(providerID: string) {
  return apiRequest<{ grants: ProProviderCreditGrant[] }>(
    `${proProviderCreditPath(providerID)}/credit-grants`,
    { method: "GET", cache: "no-store", timeoutMs: 20_000 },
  );
}

export async function createProProviderCreditGrant(
  providerID: string,
  grant: ProProviderCreditGrantInput,
  idempotencyKey: string,
) {
  return apiRequest<{ grant: ProProviderCreditGrant }>(
    `${proProviderCreditPath(providerID)}/credit-grants`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: grant,
      timeoutMs: 20_000,
    },
  );
}

export function proProviderLabel(slug: string): string {
  return PRO_PROVIDER_OPTIONS.find((option) => option.value === slug)?.label || slug || "Provider";
}
